import createPassiveAction from 'model/actions/utilities/createPassiveAction';
import levelUp from './events/levelUp';

// Three acts on a floor. The first is who he asks, the second is what they come to him for,
// and the third is how far they are willing to travel to do it — distance being the only axis
// left once the path stops charging him for anything.
//
// `act` is that division as data, because beg.js reads it: the result lines are keyed on the
// act rather than the level, so the prose turns over three times across the ladder instead of
// ten. Ten registers would each go stale inside the one level that used them — beg is clicked
// dozens of times per level. Move a level between acts and its results follow.
//
// He is not a charlatan and the game never resolves whether the remedy works. Nothing here
// models that question: the concoction touches no number the player can see, because a number
// would be an answer. What is modelled is the second-order effect — reputation, charisma, and
// eventually a region's tithe — which is real either way. Belief has mechanics. Efficacy does
// not, and there is nothing in this file to datamine.

// What arrives without being asked for. Nothing in the first act: a man with a tin gets what
// he is handed and no more. From Root Digger on it is things left at a door, and by Shepherd
// it is a tithe carried out from towns he has never walked through.
//
// This column is where the path grows, and it is deliberately uncountered. It starts at
// nothing and ends as the whole economy — begging pays less at the top of the ladder than the
// tithe does — and the game never charges him for it or remarks on it.
const given = (food, coins) => createPassiveAction({ resources: { food, coins } });

// The level-up modal is found by matching the level's name inside the event title, so the two
// have to move together. No name here is a substring of another.
const ascend = name => (context, setEvent) => levelUp(context, setEvent).find(event => event.title.includes(name));

// Every `actionLabel` carries a `b`, and leads with one. The action's hotkey is `b` at all ten
// levels, and `Button.underlineHotkey` marks the key by finding that letter in the label — no
// letter, no underline, and the key still fires but silently. There are no tooltips that teach,
// so the underline is the only place the hotkey is written down. The crime ladder holds its `r`
// the same way.
//
// The verb turns over when the act does, and repeats inside one: `Beg` for the whole first act,
// `Bless` for the last two levels of the third, where the only thing still changing is how far
// the words carry. `theme.buttonRamp` is steepest at the top of the ladder, so the level that
// shares a label is the one whose button moves most.

// `bonus` stops at .25 because beg.js saturates past it — success there is
// `(0.7 + charisma × 0.01 + bonus) × generosity`, so any value carrying that sum over 1.0 buys
// nothing. The old curve ran to 1.1 and the top half of the ladder was numerically inert.
// Growth belongs in `dailyBenefits` instead.
const charity = {
  label: 'Charity',
  levels: [
    {
      name: 'Stray',
      minExperience: 0,
      act: 1,
      actionLabel: 'Beg',
      description: 'a hand out and a road behind',
      bonus: 0.0,
      onLevelUp: null,
      randomEvents: [],
      dailyBenefits: []
    },
    {
      name: 'Beggar',
      minExperience: 20,
      act: 1,
      actionLabel: 'Beg',
      description: 'asks whoever comes. mostly wrong',
      bonus: 0.03,
      onLevelUp: null,
      randomEvents: [],
      dailyBenefits: []
    },
    {
      name: 'Regular',
      minExperience: 50,
      act: 1,
      actionLabel: 'Beg',
      description: 'the same corner. they know the face',
      bonus: 0.06,
      onLevelUp: null,
      randomEvents: [],
      dailyBenefits: []
    },
    {
      name: 'Mendicant',
      minExperience: 90,
      act: 1,
      actionLabel: 'Beg',
      description: 'asks plain now. does not look away',
      bonus: 0.1,
      onLevelUp: ascend('Mendicant'),
      randomEvents: [],
      dailyBenefits: []
    },
    {
      name: 'Root Digger',
      minExperience: 140,
      act: 2,
      actionLabel: 'Brew',
      description: 'follows what the animals eat',
      bonus: 0.13,
      onLevelUp: ascend('Root Digger'),
      randomEvents: [],
      dailyBenefits: [given(0.2, 0)]
    },
    {
      name: 'Bottle Man',
      minExperience: 200,
      act: 2,
      actionLabel: 'Bottle',
      description: 'carries it. gives it to whoever is coughing',
      bonus: 0.16,
      onLevelUp: ascend('Bottle Man'),
      randomEvents: [],
      dailyBenefits: [given(0.5, 0.3)]
    },
    {
      name: 'Healer',
      minExperience: 280,
      act: 3,
      actionLabel: 'Bind',
      description: "the town's word for him. not his",
      bonus: 0.2,
      onLevelUp: ascend('Healer'),
      randomEvents: [],
      dailyBenefits: [given(1, 1)]
    },
    {
      name: 'Man of God',
      minExperience: 380,
      act: 3,
      actionLabel: 'Baptize',
      description: 'they come from the next valley',
      bonus: 0.22,
      onLevelUp: ascend('Man of God'),
      randomEvents: [],
      dailyBenefits: [given(2, 3)]
    },
    {
      name: 'Preacher',
      minExperience: 500,
      act: 3,
      actionLabel: 'Bless',
      description: "three days' ride to hear it said",
      bonus: 0.24,
      onLevelUp: ascend('Preacher'),
      randomEvents: [],
      dailyBenefits: [given(3, 6)]
    },
    {
      name: 'Shepherd',
      minExperience: 650,
      act: 3,
      actionLabel: 'Bless',
      description: 'places he has never walked send riders',
      bonus: 0.25,
      onLevelUp: ascend('Shepherd'),
      randomEvents: [],
      dailyBenefits: [given(5, 12)]
    }
  ],
  traitMultipliers: {
    charisma: 0.05
  }
};

export default charity;
